import React from "react";
import { useEffect, useState } from "react";
import { useForm } from "@tanstack/react-form";

import {
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  ensureV2DatepickerPortal,
  RadioGroup,
  Select,
  TextField,
} from "../../../ui/index.ts";
import {
  BLOOD_GROUP_VALUES,
  MARITAL_STATUS_VALUES,
  personalStepSchema,
  type PersonalStepValues,
} from "../../create-employee/schemas/create-employee.schema.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";
import { useHrSetupUpdateEmployee } from "../hooks/use-hr-setup-update-employee.ts";
import type { HrSetupEmployeeRow } from "../schemas/hr-setup-employee.schema.ts";
import { mapOnboardUserToPersonalStep, mapOnboardUserToContactStep } from "../utils/map-onboard-user-to-form.ts";
import { ApiError } from "../../../../utils/api.ts";
import { readOnlyFieldValue } from "../../../../utils/read-only-field-value.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";

import styles from "./hr-personal-detail-tab.module.css";
import {
  isValidSsn,
  SSN_VALIDATION_MESSAGE,
} from "../../../../utils/ssn-validation.ts";

export type HrPersonalDetailTabProps = {
  employee: HrSetupEmployeeRow;
  usesUsIdentityFields: boolean;
  onSuccess: () => void;
};

const field = (name: keyof PersonalStepValues) => name;

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "others", label: "Others" },
];

const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseIsoToDate(iso: string): Date | null {
  if (iso == null || iso.trim() === "") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function formatIsoDateDisplay(iso: string): string {
  const dt = parseIsoToDate(iso);
  if (!dt) return "";
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${dt.getFullYear()}`;
}

function digitsOnly(value: string, maxLen: number): string {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

function nameCharactersOnly(value: string): string {
  return value.replace(/[^a-zA-Z ]/g, "");
}


export function HrPersonalDetailTab({ employee, usesUsIdentityFields, onSuccess }: HrPersonalDetailTabProps) {
  const [editing, setEditing] = useState(false);
  const { updateEmployee, loading } = useHrSetupUpdateEmployee();

  const today = new Date();
  const todayStart = startOfDay(today);
  const minJoinCal = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
  const joiningMinStart = startOfDay(
    new Date(minJoinCal.getFullYear(), minJoinCal.getMonth(), minJoinCal.getDate()),
  );
  const childDobMin = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate() + 1,
  );

  useEffect(() => {
    ensureV2DatepickerPortal();
  }, []);

  const mappedPersonalDetails = mapOnboardUserToPersonalStep(employee);
  const initialPersonalDetails = usesUsIdentityFields
    ? {
      ...mappedPersonalDetails,
      ssn: digitsOnly(mappedPersonalDetails.ssn ?? "", 9),
    }
    : mappedPersonalDetails;

  const form = useForm({
    defaultValues: initialPersonalDetails,
    validators: usesUsIdentityFields ? undefined : { onSubmit: personalStepSchema as never },
    onSubmit: async ({ value }) => {
      if (usesUsIdentityFields && !isValidSsn(value.ssn)) {
        showError(SSN_VALIDATION_MESSAGE);
        return;
      }
      if (usesUsIdentityFields && !/^\d{5}$/.test((value.zipCode ?? "").trim())) {
        showError("Enter a valid 5-digit ZIP Code (e.g., 12345).");
        return;
      }
      if (
        usesUsIdentityFields &&
        value.passportNumber?.trim() &&
        !/^[A-Z0-9]{6,9}$/.test(value.passportNumber.trim().toUpperCase())
      ) {
        showError(
          "Enter a valid US passport number with 6 to 9 letters or digits (for example, A12345678).",
        );
        return;
      }
      try {
        await updateEmployee({
          ...mapOnboardUserToContactStep(employee),
          ...value,
          id: employee.id,
          usesUsIdentityFields,
        });
        showSuccess("Employee details updated.");
        setEditing(false);
        onSuccess();
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Failed to update employee.";
        showError(msg);
      }
    },
  });

  const readOnly = !editing;
  const fieldDisabled = readOnly || loading;

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <div className={styles.toolbar}>
        {!editing ? (
          <Button
            type="button"
            variant="muted-outline"
            size="sm"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        ) : null}
      </div>

      <div className={styles.grid}>
        <form.Field name={field("firstName")}>
          {(f) => (
            <TextField
              id="hr-setup-personal-first-name"
              label="First Name"
              value={f.state.value}
              maxLength={100}
              onChange={(e) => f.handleChange(e.target.value)}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("middleName")}>
          {(f) =>
            readOnly ? (
              <TextField
                id="hr-setup-personal-middle-name"
                label="Middle Name"
                value={readOnlyFieldValue(f.state.value, readOnly, {
                  optional: true,
                })}
                readOnly
                disabled
              />
            ) : (
              <TextField
                id="hr-setup-personal-middle-name"
                label="Middle Name"
                value={f.state.value ?? ""}
                maxLength={100}
                onChange={(e) => f.handleChange(e.target.value)}
                onBlur={f.handleBlur}
                error={formatFormFieldErrors(f.state.meta.errors)}
                disabled={fieldDisabled}
              />
            )
          }
        </form.Field>
        <form.Field name={field("lastName")}>
          {(f) => (
            <TextField
              id="hr-setup-personal-last-name"
              label="Last Name"
              value={f.state.value}
              maxLength={100}
              onChange={(e) => f.handleChange(e.target.value)}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("personalEmail")}>
          {(f) => (
            <TextField
              id="hr-setup-personal-email"
              label="Personal Email Address"
              type="email"
              value={f.state.value}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled
              readOnly
              required
            />
          )}
        </form.Field>
        <div className={styles.spanFull}>
          <form.Field name={field("gender")}>
            {(f) => (
              <RadioGroup
                id="hr-setup-personal-gender"
                name="hr-setup-personal-gender"
                label="Gender"
                required
                options={GENDER_OPTIONS}
                value={f.state.value ?? ""}
                onChange={(v) => f.handleChange(v)}
                onBlur={f.handleBlur}
                error={formatFormFieldErrors(f.state.meta.errors)}
                disabled={fieldDisabled}
              />
            )}
          </form.Field>
        </div>
        {!usesUsIdentityFields ? <form.Field name={field("bloodGroup")}>
          {(f) =>
            readOnly ? (
              <TextField
                id="hr-setup-personal-blood-group"
                label="Blood Group"
                value={f.state.value ?? ""}
                readOnly
                disabled
                required
              />
            ) : (
              <Select
                id="hr-setup-personal-blood-group"
                label="Blood Group"
                required
                value={f.state.value ?? ""}
                onChange={(v) => f.handleChange(v)}
                options={BLOOD_GROUP_VALUES.map((bg) => ({
                  value: bg,
                  label: bg,
                }))}
                placeholder="Select Blood Group"
                disabled={fieldDisabled}
                error={formatFormFieldErrors(f.state.meta.errors)}
              />
            )
          }
        </form.Field> : null}
        <form.Field name={field("dob")}>
          {(f) =>
            readOnly ? (
              <TextField
                id="hr-setup-personal-dob"
                label="Date of Birth"
                value={formatIsoDateDisplay(f.state.value ?? "")}
                readOnly
                disabled
                required
              />
            ) : (
              <DatePicker
                id="hr-setup-personal-dob"
                label="Date of Birth"
                required
                disabled={fieldDisabled}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                maxDate={todayStart}
                selected={parseIsoToDate(f.state.value ?? "")}
                onChange={(date) => f.handleChange(localIsoDate(date))}
                customInput={<DatePickerFieldTrigger />}
                error={formatFormFieldErrors(f.state.meta.errors)}
              />
            )
          }
        </form.Field>
        <form.Field name={field(usesUsIdentityFields ? "ssn" : "aadharNumber")}>
          {(f) => (
            <TextField
              id={usesUsIdentityFields ? "hr-setup-personal-ssn" : "hr-setup-personal-aadhar"}
              label={usesUsIdentityFields ? "SSN (Social Security Number)" : "Aadhar Number"}
              inputMode="numeric"
              maxLength={usesUsIdentityFields ? 9 : 12}
              placeholder={
                usesUsIdentityFields ? "Enter SSN" : "Enter Aadhar number"
              }
              value={
                usesUsIdentityFields
                  ? digitsOnly(f.state.value ?? "", 9)
                  : f.state.value
              }
              onChange={(e) =>
                f.handleChange(
                  digitsOnly(e.target.value, usesUsIdentityFields ? 9 : 12),
                )
              }
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field(usesUsIdentityFields ? "zipCode" : "panCardNumber")}>
          {(f) => (
            <TextField
              id={usesUsIdentityFields ? "hr-setup-personal-zip" : "hr-setup-personal-pan"}
              label={usesUsIdentityFields ? "ZIP Code" : "PAN Card Number"}
              maxLength={usesUsIdentityFields ? 5 : 10}
              placeholder={
                usesUsIdentityFields
                  ? "Enter ZIP code"
                  : "Enter PAN card number"
              }
              value={f.state.value}
              onChange={(e) => f.handleChange(usesUsIdentityFields ? digitsOnly(e.target.value, 5) : e.target.value.toUpperCase())}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("passportNumber")}>
          {(f) =>
            readOnly ? (
              <TextField
                id="hr-setup-personal-passport"
                label="Passport Number (If Applicable)"
                value={readOnlyFieldValue(f.state.value, readOnly, {
                  optional: true,
                })}
                readOnly
                disabled
              />
            ) : (
              <TextField
                id="hr-setup-personal-passport"
                label="Passport Number (If Applicable)"
                maxLength={usesUsIdentityFields ? 9 : 12}
                placeholder="Enter passport number"
                value={f.state.value ?? ""}
                onChange={(e) =>
                  f.handleChange(
                    usesUsIdentityFields
                      ? e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 9)
                      : e.target.value.slice(0, 12),
                  )
                }
                onBlur={f.handleBlur}
                error={formatFormFieldErrors(f.state.meta.errors)}
                disabled={fieldDisabled}
              />
            )
          }
        </form.Field>
        <form.Field name={field("joiningDate")}>
          {(f) =>
            readOnly ? (
              <TextField
                id="hr-setup-personal-joining-date"
                label="Date of Joining"
                value={formatIsoDateDisplay(f.state.value ?? "")}
                readOnly
                disabled
                required
              />
            ) : (
              <DatePicker
                id="hr-setup-personal-joining-date"
                label="Date of Joining"
                required
                disabled={fieldDisabled}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                minDate={joiningMinStart}
                maxDate={todayStart}
                selected={parseIsoToDate(f.state.value ?? "")}
                onChange={(date) => f.handleChange(localIsoDate(date))}
                customInput={<DatePickerFieldTrigger />}
                error={formatFormFieldErrors(f.state.meta.errors)}
              />
            )
          }
        </form.Field>
        <form.Field name={field("maritalStatus")}>
          {(f) =>
            readOnly ? (
              <TextField
                id="hr-setup-personal-marital-status"
                label="Marital Status"
                value={f.state.value ?? ""}
                readOnly
                disabled
                required
              />
            ) : (
              <Select
                id="hr-setup-personal-marital-status"
                label="Marital Status"
                required
                value={f.state.value ?? ""}
                onChange={(v) => {
                  f.handleChange(v);
                  if (v !== "Married") {
                    form.setFieldValue("spouseName", "");
                    form.setFieldValue("spouseDob", "");
                    form.setFieldValue("anniversaryDate", "");
                    form.setFieldValue("hasChild", undefined);
                    form.setFieldValue("childName", "");
                    form.setFieldValue("childDob", "");
                    form.setFieldValue("childGender", undefined);
                  }
                }}
                options={MARITAL_STATUS_VALUES.map((s) => ({
                  value: s,
                  label: s,
                }))}
                placeholder="Select Marital Status"
                disabled={fieldDisabled}
                error={formatFormFieldErrors(f.state.meta.errors)}
              />
            )
          }
        </form.Field>
        <form.Subscribe selector={(s) => s.values.maritalStatus}>
          {(maritalStatus) =>
            maritalStatus === "Married" && !usesUsIdentityFields ? (
              <>
                <form.Field name={field("spouseName")}>
                  {(f) => (
                    <TextField
                      id="hr-setup-personal-spouse-name"
                      label="Spouse Name"
                      required={!readOnly}
                      maxLength={100}
                      value={
                        readOnly
                          ? readOnlyFieldValue(f.state.value, true, { optional: true })
                          : (f.state.value ?? "")
                      }
                      onChange={(e) => {
                        f.handleChange(nameCharactersOnly(e.target.value));
                      }}

                      onBlur={f.handleBlur}
                      error={formatFormFieldErrors(f.state.meta.errors)}
                      readOnly={readOnly}
                      disabled={fieldDisabled}
                    />
                  )}
                </form.Field>
                <form.Field name={field("spouseDob")}>
                  {(f) =>
                    readOnly ? (
                      <TextField
                        id="hr-setup-personal-spouse-dob"
                        label="Spouse Date of Birth"
                        value={readOnlyFieldValue(
                          formatIsoDateDisplay(f.state.value ?? ""),
                          readOnly,
                          { optional: true },
                        )}
                        readOnly
                        disabled
                      />
                    ) : (
                      <DatePicker
                        id="hr-setup-personal-spouse-dob"
                        label="Spouse Date of Birth"
                        required
                        disabled={fieldDisabled}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-mm-yyyy"
                        maxDate={todayStart}
                        selected={parseIsoToDate(f.state.value ?? "")}
                        onChange={(date) => f.handleChange(localIsoDate(date))}
                        customInput={<DatePickerFieldTrigger />}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                      />
                    )
                  }
                </form.Field>
                <form.Field name={field("anniversaryDate")}>
                  {(f) =>
                    readOnly ? (
                      <TextField
                        id="hr-setup-personal-anniversary"
                        label="Anniversary Date"
                        value={readOnlyFieldValue(
                          formatIsoDateDisplay(f.state.value ?? ""),
                          readOnly,
                          { optional: true },
                        )}
                        readOnly
                        disabled
                      />
                    ) : (
                      <DatePicker
                        id="hr-setup-personal-anniversary"
                        label="Anniversary Date"
                        required
                        disabled={fieldDisabled}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-mm-yyyy"
                        maxDate={todayStart}
                        selected={parseIsoToDate(f.state.value ?? "")}
                        onChange={(date) => f.handleChange(localIsoDate(date))}
                        customInput={<DatePickerFieldTrigger />}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                      />
                    )
                  }
                </form.Field>
                <div className={styles.spanFull}>
                  <form.Field name={field("hasChild")}>
                    {(f) => (
                      <RadioGroup
                        id="hr-setup-personal-has-child"
                        name="hr-setup-personal-has-child"
                        label="Do you have a child below 18 years of age?"
                        required={!readOnly}
                        options={YES_NO_OPTIONS}
                        value={f.state.value ?? "no"}
                        onChange={(v) => {
                          f.handleChange(v as "yes" | "no");
                          if (v !== "yes") {
                            form.setFieldValue("childName", "");
                            form.setFieldValue("childDob", "");
                            form.setFieldValue("childGender", undefined);
                          }
                        }}
                        onBlur={f.handleBlur}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                        disabled={fieldDisabled}
                      />
                    )}
                  </form.Field>
                </div>
                <form.Subscribe selector={(s) => s.values.hasChild}>
                  {(hasChild) =>
                    hasChild === "yes" ? (
                      <>
                        <form.Field name={field("childName")}>
                          {(f) => (
                            <TextField
                              id="hr-setup-personal-child-name"
                              label="Child Name"
                              required={!readOnly}
                              maxLength={100}
                              value={
                                readOnly
                                  ? readOnlyFieldValue(f.state.value, true, { optional: true })
                                  : (f.state.value ?? "")
                              }
                              onChange={(e) =>
                                f.handleChange(nameCharactersOnly(e.target.value))
                              }

                              onBlur={f.handleBlur}
                              error={formatFormFieldErrors(f.state.meta.errors)}
                              readOnly={readOnly}
                              disabled={fieldDisabled}
                            />
                          )}
                        </form.Field>
                        <form.Field name={field("childDob")}>
                          {(f) =>
                            readOnly ? (
                              <TextField
                                id="hr-setup-personal-child-dob"
                                label="Child Date of Birth"
                                value={readOnlyFieldValue(
                                  formatIsoDateDisplay(f.state.value ?? ""),
                                  readOnly,
                                  { optional: true },
                                )}
                                readOnly
                                disabled
                              />
                            ) : (
                              <DatePicker
                                id="hr-setup-personal-child-dob"
                                label="Child Date of Birth"
                                required
                                disabled={fieldDisabled}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="dd-mm-yyyy"
                                minDate={childDobMin}
                                maxDate={todayStart}
                                selected={parseIsoToDate(f.state.value ?? "")}
                                onChange={(date) => f.handleChange(localIsoDate(date))}
                                customInput={<DatePickerFieldTrigger />}
                                error={formatFormFieldErrors(f.state.meta.errors)}
                              />
                            )
                          }
                        </form.Field>
                        <form.Field name={field("childGender")}>
                          {(f) => (
                            <RadioGroup
                              id="hr-setup-personal-child-gender"
                              name="hr-setup-personal-child-gender"
                              label="Child Gender"
                              required={!readOnly}
                              options={GENDER_OPTIONS}
                              value={f.state.value ?? ""}
                              onChange={(v) =>
                                f.handleChange(v as "male" | "female" | "others")
                              }
                              onBlur={f.handleBlur}
                              error={formatFormFieldErrors(f.state.meta.errors)}
                              disabled={fieldDisabled}
                            />
                          )}
                        </form.Field>
                      </>
                    ) : null
                  }
                </form.Subscribe>
              </>
            ) : null
          }
        </form.Subscribe>
        {usesUsIdentityFields ? (
          <form.Subscribe selector={(s) => s.values.maritalStatus}>
            {(maritalStatus) =>
              maritalStatus === "Married" ? (
                <form.Field name={field("anniversaryDate")}>
                  {(f) =>
                    readOnly ? (
                      <TextField
                        id="hr-setup-personal-anniversary"
                        label="Anniversary Date"
                        value={readOnlyFieldValue(
                          formatIsoDateDisplay(f.state.value ?? ""),
                          readOnly,
                          { optional: true },
                        )}
                        readOnly
                        disabled
                      />
                    ) : (
                      <DatePicker
                        id="hr-setup-personal-anniversary"
                        label="Anniversary Date"
                        disabled={fieldDisabled}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-mm-yyyy"
                        maxDate={todayStart}
                        selected={parseIsoToDate(f.state.value ?? "")}
                        onChange={(date) => f.handleChange(localIsoDate(date))}
                        customInput={<DatePickerFieldTrigger />}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                      />
                    )
                  }
                </form.Field>
              ) : null
            }
          </form.Subscribe>
        ) : null}
      </div>

      <div className={styles.actions}>
        {editing ? (
          <>
            <Button
              type="button"
              variant="muted-outline"
              disabled={loading}
              onClick={() => {
                form.reset(initialPersonalDetails);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Updating…" : "Update"}
            </Button>
          </>
        ) : (
          <div className={styles.filler}></div>
        )}
      </div>
    </form>
  );
}
